import { html, css } from 'lit-element';
import { BaseElement } from './BaseElement';
import { faExclamationTriangle, faInfoCircle, faQuestionCircle } from '@fortawesome/free-solid-svg-icons';

const DEFAULT_TITLE = 'Default Title';
const DEFAULT_MESSAGE = 'Default Message';
const DEFAULT_HEIGHT = 'auto';
const DEFAULT_WIDTH = '20em';
const DEFAULT_AUTO_OPEN = true;
const DEFAULT_REMOVE_ON_CLOSE = true;
const DEFAULT_CALLBACK = () => {};
const DEFAULT_CONTENTS = null;

export const DIALOG_TYPE_INFO = 0;
export const DIALOG_TYPE_ERROR = 1;
export const DIALOG_TYPE_CONFIRM = 2;
export const DEFAULT_DIALOG_TYPE = DIALOG_TYPE_INFO;
export const DIALOG_TYPES = {
	DIALOG_TYPE_INFO,
	DIALOG_TYPE_ERROR,
	DIALOG_TYPE_CONFIRM
};

export const BUTTON_OK = 0;
export const BUTTON_CANCEL = 1;

const _document = Symbol();
const _title = Symbol();
const _message = Symbol();
const _contents = Symbol();
const _type = Symbol();
const _width = Symbol();
const _height = Symbol();
const _autoOpen = Symbol();
const _removeOnClose = Symbol();
const _isOpen = Symbol();
const _keyDownListener = Symbol();
const _isConnected = Symbol();
const _callback = Symbol();
const _priorActiveElement = Symbol();

const openStack = [];

export class ModalDialog extends BaseElement {
	constructor() {
		super();

		this[_keyDownListener] = (e) => {
			//Do nothing if we're not the last item on the stack
			if(openStack.indexOf(this) !== openStack.length - 1)
				return;
			
			if(e.key === 'Escape') {
				this.close(null);
			}
			if(e.composedPath().indexOf(this) === -1) {
				e.preventDefault();
			}
		};

		this.library.add(faExclamationTriangle, faInfoCircle, faQuestionCircle);

		this.title = DEFAULT_TITLE;
		this.message = DEFAULT_MESSAGE;
		this.type = DEFAULT_DIALOG_TYPE;
		this.width = DEFAULT_WIDTH;
		this.height = DEFAULT_HEIGHT;
		this[_isOpen] = DEFAULT_AUTO_OPEN;
		this.removeOnClose = DEFAULT_REMOVE_ON_CLOSE;
		this[_isConnected] = false;
		this.priorActiveElement = null;
		this.callback = DEFAULT_CALLBACK;
		this.contents = DEFAULT_CONTENTS;
	}
	static get styles() {
		return [
			...BaseElement.styles,
			css`
				:host {
					display: block;
					color: var(--theme-primary, black);
					background: var(--theme-secondary, white);
					border: var(--theme-border, black);
					font-family: var(--theme-font-family, Arial);
					font-size: var(--theme-font-size, 16px);
				}
				.overlay {
					position: fixed;
					top: 0;
					right: 0;
					bottom: 0;
					left: 0;
					background-color: #000;
					opacity: 0.5;
					animation: 0.5s 1 fade-in;
				}
				@keyframes fade-in {
					from {
						opacity: 0;
					}
					to {
						opacity: 0.5;
					}
				}
				button {
					padding: 0.5em;
				}
				.main {
					display: flex;
					align-items: center;
					justify-content: center;
					position: fixed;
					top: 0;
					right: 0;
					bottom: 0;
					left: 0;
				}
				.main .dialog {
					background: #fff;
					// border: 1px solid black;
					border-radius: 5px;
					margin: 1em;
				}
				.main .dialog .title {
					margin: 0.5em 0.5em 0 0.5em;
					border-radius: 5px 5px 0 0;
				}
				.main .dialog .title .icon {
					// height: 1em;
					// position: relative;
					// top: .15em;
					margin-right: 0.5em;
				}
				.main .dialog .contents {
					margin: 0.5em;
				}
				.main .dialog .buttons {
					margin: 0.5em;
					text-align: right;
				}
			`
		];
	}
	get document() {
		return this[_document];
	}
	set document(v) {
		if(v !== null && !(v instanceof HTMLDocument))
			throw new TypeError('document must be an instance of HTMLDocument.');
		this[_document] = v;
	}
	get message() {
		return this[_message];
	}
	set message(v) {
		if(typeof v !== 'string')
			throw new TypeError('message must be a string.');
		this[_message] = v;
	}
	get type() {
		return this[_type];
	}
	set type(v) {
		if(Object.values(DIALOG_TYPES).indexOf(v) === -1)
			throw new TypeError(`type must be one of ${Object.keys(DIALOG_TYPES).join(', ')}`);
		this[_type] = v;
	}
	get width() {
		return this[_width];
	}
	set width(v) {
		if(typeof v !== 'string')
			throw new TypeError('width must be a string.');
		this[_width] = v;
		this.requestUpdate();
	}
	get height() {
		return this[_height];
	}
	set height(v) {
		if(typeof v !== 'string')
			throw new TypeError('height must be a string.');
		this[_height] = v;
	}
	get title() {
		return this[_title];
	}
	set title(v) {
		if(typeof v !== 'string')
			throw new TypeError('title must be a string.');
		this[_title] = v;
	}
	get autoOpen() {
		return this[_autoOpen];
	}
	set autoOpen(v) {
		if(typeof v !== 'boolean')
			throw new TypeError('autoOpen must be a boolean.');
		this[_autoOpen] = v;
	}
	get removeOnClose() {
		return this[_removeOnClose];
	}
	set removeOnClose(v) {
		if(typeof v !== 'boolean')
			throw new TypeError('removeOnClose must be a boolean.');
		this[_removeOnClose] = v;
	}
	get callback() {
		return this[_callback];
	}
	set callback(v) {
		if(typeof v !== 'function')
			throw new TypeError('callback must be a function.');
		this[_callback] = v;
	}
	get contents() {
		return this[_contents];
	}
	set contents(v) {
		this[_contents] = v;
	}
	get priorActiveElement() {
		return this[_priorActiveElement];
	}
	set priorActiveElement(v) {
		if(v !== null && !(v instanceof HTMLElement))
			throw new TypeError('priorActiveElement must be an instance of HTMLElement.');
		this[_priorActiveElement] = v;
	}
	get isOpen() {
		return this[_isOpen];
	}
	get isConnected() {
		return this[_isConnected];
	}
	connectedCallback() {
		super.connectedCallback();

		this[_isConnected] = true;
		if(this.isOpen)
			this.open();
	}
	disconnectedCallback() {
		super.disconnectedCallback();

		this[_isConnected] = false;
		// this.close();
	}
	async close(button) {
		if(!this.document)
			throw new Error('document property not set!');
		
		//Run the callback if the return value is false, do nothing
		if(await this.callback(button, this) === false)
			return;
		
		this[_isOpen] = false;

		//Add a keydown event listener
		this.document.removeEventListener('keydown', this[_keyDownListener]);

		//Make this invisible
		this.style.display = 'none';
		
		// Remove from the parent if required
		if(this.removeOnClose)
			this.remove();

		//Remove this dialog from the open stack
		openStack.splice(openStack.indexOf(this), 1);

		//Re-focus the prior active element if there was one.
		if(this.priorActiveElement) {
			this.priorActiveElement.focus();
			this.priorActiveElement = null;
		}
	}
	open() {
		if(!this.document)
			throw new Error('document property not set!');
		
		this[_isOpen] = true;

		//Listen to keydown events
		this.document.addEventListener('keydown', this[_keyDownListener]);

		//Make this visible
		this.style.display = '';

		//Add this dialog to the open stack
		openStack.push(this);

		//Capture the current active element
		let activeElement = this.document.activeElement;
		while(activeElement.shadowRoot) {
			activeElement = activeElement.shadowRoot.activeElement;
		}
		this.priorActiveElement = activeElement;
	}
	handleButtonClick(e) {
		if(!(e.target instanceof HTMLButtonElement))
			return;

		this.close(parseInt(e.target.dataset.type, 10));
	}
	updated() {
		this.shadowRoot.querySelector('div.main').focus();
		const dialog = this.shadowRoot.querySelector('div.dialog');
		dialog.style.width = this.width;
		dialog.style.height = this.height;
	}
	render() {
		let icon = '';
		switch(this.type) {
			case DIALOG_TYPE_ERROR:
				icon = 'fa-exclamation-triangle';
				break;
			case DIALOG_TYPE_INFO:
				icon = 'fa-info-circle';
				break;
			case DIALOG_TYPE_CONFIRM:
				icon = 'fa-question-circle';
				break;
		}
		
		return html`
			<div class="overlay"></div>
			<div class="main" tabindex="0">
				<div class="dialog">
					<div class="title"><i class="icon fas ${icon}"></i>${this.title}</div>
					<hr/>
					<div class="contents">
						${this.message}
						${this.contents}
					</div>
					<div @click="${e => this.handleButtonClick(e)}" class="buttons">
						<button data-type="${BUTTON_OK}">OK</button>
						${this.type === DIALOG_TYPE_CONFIRM ? html`<button data-type="${BUTTON_CANCEL}">Cancel</button>` : ''}
					</div>
				</div>
			</div>
		`;
	}
}

customElements.define('pp-modal-dialog', ModalDialog);